import { lazy, Suspense, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';

import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import RoutesMap from '@/enums/RoutesMap';
import useTasks from '@/hooks/useTasks';
import { useIsChatOpen, useChatActions } from '@/store/chat/chatStore';
import { useIsGoogleDriveOpen, useGoogleDriveActions } from '@/store/googleDriveStore';

const ModalsManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refetchTasks } = useTasks();
  const { refetchUserDetails } = useLoginContext();
  const isChatOpen = useIsChatOpen();
  const { resetChat, setIsChatOpen } = useChatActions();
  const isGoogleDriveOpen = useIsGoogleDriveOpen();
  const { closeGoogleDrive } = useGoogleDriveActions();
  const lazyModals = useMemo(
    () => ({
      LazyChatModal: lazy(() => import('@/components/modals/ChatModal/ChatModal')),
      LazyTasksModal: lazy(() => import('@/components/modals/TasksModal/TasksModal')),
      LazyPersonalSettingsModal: lazy(
        () => import('@/components/modals/PersonalSeetingsModal/PersonalSettingsModal')
      ),
      LazyCompanyProfileModal: lazy(
        () => import('@/components/modals/CompanyProfileModal/CompanyProfileModal')
      ),
      LazyIntegrationsModal: lazy(
        () => import('@/components/modals/IntegrationsModal/IntegrationsModal')
      ),
      LazyCalendarModal: lazy(() => import('@/components/modals/CalendarModal/CalendarModal')),
      LazyGoogleDriveModal: lazy(
        () => import('@/components/modals/googleDriveModal/GoogleDriveModal')
      )
    }),
    []
  );

  const RouteRefetchMap = {
    [RoutesMap.Tasks]: refetchTasks,
    [RoutesMap.CompanyProfile]: refetchUserDetails,
    [RoutesMap.PersonalSettings]: refetchUserDetails
  };

  const onClose = (route: RoutesMap) => {
    if (route && route in RouteRefetchMap) {
      RouteRefetchMap[route as keyof typeof RouteRefetchMap]();
    }
    navigate(-1);
  };

  const onCloseCalendar = useCallback(
    (reason?: string) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        navigate(RoutesMap.Home);
      }
    },
    [navigate]
  );

  return (
    <>
      <Outlet />
      <AnimatePresence onExitComplete={resetChat}>
        {isChatOpen && (
          <Suspense key="chat-modal">
            <lazyModals.LazyChatModal open onClose={() => setIsChatOpen(false)} />
          </Suspense>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isGoogleDriveOpen && (
          <Suspense key="google-drive-modal">
            <lazyModals.LazyGoogleDriveModal open onClose={() => closeGoogleDrive()} />
          </Suspense>
        )}
        {location.pathname === RoutesMap.Tasks && (
          <Suspense key="tasks-modal">
            <lazyModals.LazyTasksModal onClose={() => onClose(RoutesMap.Tasks)} open />
          </Suspense>
        )}
        {location.pathname === RoutesMap.Calendar && (
          <Suspense key="calendar-modal">
            <lazyModals.LazyCalendarModal onClose={onCloseCalendar} open />
          </Suspense>
        )}
      </AnimatePresence>
      {location.pathname === RoutesMap.CompanyProfile && (
        <Suspense>
          <lazyModals.LazyCompanyProfileModal
            onClose={() => onClose(RoutesMap.CompanyProfile)}
            onConfirm={() => onClose(RoutesMap.CompanyProfile)}
            open
          />
        </Suspense>
      )}
      {location.pathname === RoutesMap.PersonalSettings && (
        <Suspense>
          <lazyModals.LazyPersonalSettingsModal
            onClose={() => onClose(RoutesMap.PersonalSettings)}
            onConfirm={() => onClose(RoutesMap.PersonalSettings)}
            open
          />
        </Suspense>
      )}
      {location.pathname === RoutesMap.Integrations && (
        <Suspense>
          <lazyModals.LazyIntegrationsModal onClose={() => navigate(-1)} open />
        </Suspense>
      )}
    </>
  );
};

export default ModalsManager;
